import { cn, Icon } from '@lqa-frontend/ui-system';
import * as SelectPrimitive from '@radix-ui/react-select';
import * as React from 'react';
import { useResolvedPortalContainer } from '../../hooks/use-portal-container';

type SelectTriggerSize = 'sm' | 'default';

interface ISelectTriggerProps extends React.ComponentProps<
	typeof SelectPrimitive.Trigger
> {
	size?: SelectTriggerSize;
}

interface ISelectContentProps extends React.ComponentProps<
	typeof SelectPrimitive.Content
> {
	portalContainer?: HTMLElement | null;
}

function SelectRoot({
	...props
}: React.ComponentProps<typeof SelectPrimitive.Root>) {
	return <SelectPrimitive.Root data-slot="select" {...props} />;
}

function SelectGroup({
	...props
}: React.ComponentProps<typeof SelectPrimitive.Group>) {
	return <SelectPrimitive.Group data-slot="select-group" {...props} />;
}

function SelectValue({
	...props
}: React.ComponentProps<typeof SelectPrimitive.Value>) {
	return <SelectPrimitive.Value data-slot="select-value" {...props} />;
}

function SelectTrigger({
	className,
	size = 'default',
	children,
	...props
}: ISelectTriggerProps) {
	return (
		<SelectPrimitive.Trigger
			data-slot="select-trigger"
			data-size={size}
			className={cn(
				"bg-white text-text-primary border-outline-neutral data-[placeholder]:text-neutral [&_svg:not([class*='text-'])]:text-neutral focus-visible:border-interactive focus-visible:ring-interactive/50 aria-invalid:ring-danger/20 aria-invalid:border-danger flex w-fit items-center justify-between gap-8 rounded-s border px-12 py-8 text-14 whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-2 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 data-[size=default]:h-36 data-[size=sm]:h-32 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-8 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-16",
				'w-280',
				className
			)}
			{...props}
		>
			{children}
			<SelectPrimitive.Icon asChild>
				<span data-slot="select-trigger-icon" className="opacity-50">
					<Icon name="chevronDown" size="s" />
				</span>
			</SelectPrimitive.Icon>
		</SelectPrimitive.Trigger>
	);
}

function SelectContent({
	className,
	children,
	position = 'popper',
	portalContainer,
	...props
}: ISelectContentProps) {
	const resolvedContainer = useResolvedPortalContainer(portalContainer);
	return (
		<SelectPrimitive.Portal container={resolvedContainer}>
			<SelectPrimitive.Content
				data-slot="select-content"
				className={cn(
					'bg-white text-text-primary data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-[100] max-h-(--radix-select-content-available-height) min-w-128 origin-(--radix-select-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-m border shadow-md',
					position === 'popper' &&
						'data-[side=bottom]:translate-y-4 data-[side=left]:-translate-x-4 data-[side=right]:translate-x-4 data-[side=top]:-translate-y-4',
					className
				)}
				position={position}
				{...props}
			>
				<SelectScrollUpButton />
				<SelectPrimitive.Viewport
					className={cn(
						'p-4',
						position === 'popper' &&
							'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)] scroll-my-4'
					)}
				>
					{children}
				</SelectPrimitive.Viewport>
				<SelectScrollDownButton />
			</SelectPrimitive.Content>
		</SelectPrimitive.Portal>
	);
}

function SelectLabel({
	className,
	...props
}: React.ComponentProps<typeof SelectPrimitive.Label>) {
	return (
		<SelectPrimitive.Label
			data-slot="select-label"
			className={cn('px-8 py-6 text-14 font-medium', className)}
			{...props}
		/>
	);
}

function SelectItem({
	className,
	children,
	...props
}: React.ComponentProps<typeof SelectPrimitive.Item>) {
	return (
		<SelectPrimitive.Item
			data-slot="select-item"
			className={cn(
				"focus:bg-brand-blue-50 focus:text-text-primary [&_svg:not([class*='text-'])]:text-neutral relative flex w-full cursor-default items-center gap-8 rounded-s py-6 pr-44 pl-8 text-14 outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-16 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-8",
				className
			)}
			{...props}
		>
			<span className="absolute right-8 flex size-14 items-center justify-center">
				<SelectPrimitive.ItemIndicator>
					<Icon name="check" size="s" />
				</SelectPrimitive.ItemIndicator>
			</span>
			<SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
		</SelectPrimitive.Item>
	);
}

function SelectSeparator({
	className,
	...props
}: React.ComponentProps<typeof SelectPrimitive.Separator>) {
	return (
		<SelectPrimitive.Separator
			data-slot="select-separator"
			className={cn(
				'bg-outline-neutral pointer-events-none -mx-4 my-4 h-px',
				className
			)}
			{...props}
		/>
	);
}

function SelectScrollUpButton({
	className,
	...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
	return (
		<SelectPrimitive.ScrollUpButton
			data-slot="select-scroll-up-button"
			className={cn(
				'flex cursor-default items-center justify-center py-4',
				className
			)}
			{...props}
		>
			<Icon name="chevronUp" size="s" />
		</SelectPrimitive.ScrollUpButton>
	);
}

function SelectScrollDownButton({
	className,
	...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
	return (
		<SelectPrimitive.ScrollDownButton
			data-slot="select-scroll-down-button"
			className={cn(
				'flex cursor-default items-center justify-center py-4',
				className
			)}
			{...props}
		>
			<Icon name="chevronDown" size="s" />
		</SelectPrimitive.ScrollDownButton>
	);
}

const Select = Object.assign(SelectRoot, {
	Group: SelectGroup,
	Value: SelectValue,
	Trigger: SelectTrigger,
	Content: SelectContent,
	Label: SelectLabel,
	Item: SelectItem,
	Separator: SelectSeparator,
	ScrollUpButton: SelectScrollUpButton,
	ScrollDownButton: SelectScrollDownButton,
});

export { Select };
export type { ISelectTriggerProps, ISelectContentProps };
